import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { Button, EmptyState, Skeleton } from '@/components';
import { WorkspaceActivityPage } from '@/components/workspace/WorkspaceActivityPage';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { signInHref, useSession } from '@/lib/session';
import {
  workspaceMembersQueryOptions,
  workspaceQueryOptions,
  workspacesQueryOptions,
} from '@/lib/workspaces';

export const Route = createFileRoute('/w/$workspaceId/activity')({
  component: WorkspaceActivityRoute,
});

function WorkspaceActivityRoute() {
  const { workspaceId } = Route.useParams();
  const { t } = useInterfaceLanguage();
  const { profile, isPending: sessionPending, isSignedOut, error } = useSession();
  const canLoadWorkspace = !isSignedOut && !sessionPending && profile !== null;
  const workspaces = useQuery({ ...workspacesQueryOptions(), enabled: false });
  const workspace = useQuery({
    ...workspaceQueryOptions(workspaceId),
    enabled: canLoadWorkspace,
  });
  const members = useQuery({
    ...workspaceMembersQueryOptions(workspaceId),
    enabled: canLoadWorkspace,
  });
  const cachedName = workspaces.data?.find(
    (candidate) => candidate.id === workspaceId,
  )?.name;
  const detailName =
    workspace.data?.name !== undefined && workspace.data.name.trim() !== ''
      ? workspace.data.name
      : null;
  const cachedHeadingName =
    cachedName !== undefined && cachedName.trim() !== '' ? cachedName : null;
  const workspaceName = detailName ?? cachedHeadingName;

  if (isSignedOut) {
    return (
      <ActivityFrame workspaceName={workspaceName}>
        <EmptyState
          tone="neutral"
          size="panel"
          headingLevel={2}
          title={t('workspaceSettings.activity.signInTitle')}
          body={t('workspaceSettings.activity.signInBody')}
          action={
            <Button
              as="a"
              variant="primary"
              href={signInHref({
                pathname: `/w/${workspaceId}/activity`,
                search: '',
              })}
            >
              {t('workspaceSettings.activity.signInAction')}
            </Button>
          }
        />
      </ActivityFrame>
    );
  }

  if (sessionPending || members.isPending) {
    return (
      <ActivityFrame workspaceName={workspaceName}>
        <div aria-busy="true" className="flex flex-col gap-4">
          <Skeleton shape="block" count={3} />
        </div>
      </ActivityFrame>
    );
  }

  if (profile === null) {
    return (
      <ActivityFrame workspaceName={workspaceName}>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={t('workspaceSettings.activity.accountErrorTitle')}
          body={error?.message ?? t('workspaceSettings.activity.genericError')}
        />
      </ActivityFrame>
    );
  }

  if (members.isError) {
    const notFound = members.error.code === 'not_found';
    return (
      <ActivityFrame workspaceName={workspaceName}>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={
            notFound
              ? t('workspaceSettings.activity.notFoundTitle')
              : t('workspaceSettings.activity.loadFailedMembersTitle')
          }
          {...(notFound ? {} : { body: members.error.message })}
        />
      </ActivityFrame>
    );
  }

  return <WorkspaceActivityPage workspaceId={workspaceId} members={members.data} />;
}

function ActivityFrame({
  workspaceName,
  children,
}: {
  workspaceName: string | null;
  children: ReactNode;
}) {
  /*
   * ⚠ Its own `useInterfaceLanguage()`, not a `t` threaded down as a prop.
   * `activity.title` is the only key in this family with two call sites: this
   * frame draws the `<h1>` for the signed-out, pending, account-error and
   * members-error branches, and `WorkspaceActivityPage.tsx` draws the same node
   * for the loaded branch. One node, one accessible name, five branches, two
   * authors — route BOTH or the loading state speaks the translation while the
   * loaded page says `Workspace activity`, with nothing red.
   * The eyebrow above it is deliberately NOT shared. Both arms read `Workspace`
   * in English and their jobs are unrelated: `activity.workspaceFallback` is
   * this route's `??` standing in for a MISSING workspace name, while
   * `activity.eyebrow` is the loaded page's static category label. Collapsing
   * them lets a translator who renders the fallback as `(unnamed workspace)`
   * silently rewrite the page's eyebrow, and nothing here can see it.
   */
  const { t } = useInterfaceLanguage();
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 py-8">
      <header className="flex flex-col gap-2">
        <p className="text-sm font-medium text-ink-muted">
          {workspaceName ?? t('workspaceSettings.activity.workspaceFallback')}
        </p>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-ink">
          {t('workspaceSettings.activity.title')}
        </h1>
      </header>
      {children}
    </div>
  );
}
